/**
 * Wire-format types + TanStack Query keys for /api/admin/inspections/* and
 * /api/admin/assets/{id}/inspections.
 *
 * Mirrors server-side Plan 01 schemas:
 *   - InspectionRead             ↔ server.app.inspections.schemas.InspectionRead
 *   - InspectionEditPayload      ↔ server.app.inspections.schemas.InspectionEditIn
 *                                  (Pydantic `extra='forbid'`)
 *   - InspectionUpdatedResponse  ↔ server.app.inspections.schemas.InspectionUpdatedResponse
 *   - ClearPipeBlockedResponse   ↔ server.app.admin.clear_pipe_blocked_router OK envelope
 *
 * T-4-04 mitigation: InspectionEditPayload INTENTIONALLY OMITS worker_id,
 * saved_at, gps_lat, gps_lng. The server rejects them with 422 via Pydantic's
 * `extra='forbid'`; this client-side type narrowing is belt-and-suspenders.
 *
 * Plan 04 (Wave 2) does NOT modify web/src/api/assets.ts. The live-update
 * transport (SSE channel + refetchInterval policy) is owned end-to-end by
 * Plan 05 per RESEARCH.md Q1 RESOLVED — see plan summary § B6+W4 scope
 * boundary. The mutation hooks below only IMPORT `ASSETS_GEOJSON_KEY` from
 * assets.ts to drive query-cache invalidation; they do not touch the
 * `useAssetGeoJson` query config.
 */

/** TanStack Query key for a single inspection (PATCH cache invalidation). */
export const INSPECTION_KEY = (inspectionId: number) =>
  ["inspection", inspectionId] as const;

/** TanStack Query key for the per-asset inspection list (B1 fix — Plan 01 GET endpoint). */
export const ASSET_INSPECTIONS_KEY = (assetId: number) =>
  ["inspections", "asset", assetId] as const;

/**
 * Phase 6 Plan 02 — TanStack Query key for the lazy GET /api/admin/inspections/{id}/audit
 * endpoint (Plan 01 Task 3, HIST-5.3). Consumed by useInspectionAuditTrail with
 * `enabled: rowExpanded && pm_edit_count > 0` so the network call only fires
 * when PM expands a row that has any audit history.
 */
export const INSPECTION_AUDIT_KEY = (inspectionId: number) =>
  ["inspection", inspectionId, "audit"] as const;

/**
 * Phase 6 Plan 02 — TanStack Query key for the lazy GET /api/admin/inspections/{id}/photos
 * endpoint (Plan 01 Task 3, HIST-5.7). Consumed by useInspectionPhotos with
 * `enabled: rowExpanded` (no pm_edit_count gate). Invalidated on
 * `inspection.photo_uploaded` SSE events (D-137 + Plan 01 Task 2 SSE
 * event-typed pubsub) so the placeholder→thumbnail flip is live.
 */
export const INSPECTION_PHOTOS_KEY = (inspectionId: number) =>
  ["inspection", inspectionId, "photos"] as const;

export type CoatingValue = "bitumen" | "epoxy" | null;

/**
 * R3-003/D3 (audit 2026-07) — Tab-1 observed coating. 'none' is the worker's
 * explicit "no coating observed" pick; null = no signal (legacy rows / an
 * untouched pre-filled form). Tab 2's applied type keeps the narrow
 * CoatingValue — "'None' is not a valid applied value" (req.md 3.3).
 */
export type ObservedCoatingValue = "bitumen" | "epoxy" | "none" | null;

/**
 * Server -> client inspection row. Used by GET /api/admin/assets/{id}/inspections.
 *
 * Phase 6 Plan 01 (D-148, RESEARCH Finding #2 Option A) extended this shape with
 * 5 new fields: worker_name + photo_count + photo_uploaded_count are NEW;
 * gps_lat / gps_lng were already present client-side and are now aligned with
 * the server-canonical schema. `synced_at` is INTENTIONALLY ABSENT — the
 * server has no physical column and Plan 01 dropped it from InspectionRead.
 *
 * The new fields are computed server-side via dynamic ORM-attribute stamping
 * (Plan 01 SUMMARY § Patterns established) — no new physical columns.
 */
export interface InspectionRead {
  id: number;
  asset_id: number;
  worker_id: number;
  /** ISO 8601 UTC. */
  saved_at: string;
  gps_lat: number | null;
  gps_lng: number | null;
  tab1_cleaned: boolean;
  tab1_structure_damaged: boolean;
  tab1_cover_needs_level_adjustment: boolean;
  tab1_pipe_blocked: boolean;
  tab1_coating: ObservedCoatingValue;
  tab2_adjusted_cover_level: boolean;
  tab2_repaired_internal_structure: boolean;
  tab2_applied_coating: boolean;
  tab2_applied_coating_type: CoatingValue;
  pm_edit_count: number;
  edited_by_pm_at: string | null;
  edited_by_worker_at: string | null;
  // ─── Phase 6 Plan 02 — Plan 01 Task 3 InspectionRead extension ──────────
  /** Worker.name (LEFT JOIN at the repository layer; null if worker is soft-revoked + name purged). */
  worker_name: string | null;
  /** Total photo rows for this inspection (placeholder + landed). */
  photo_count: number;
  /** Subset of photo_count where uploaded_at IS NOT NULL (landed photos). */
  photo_uploaded_count: number;
}

/**
 * Phase 6 Plan 02 — single audit row from GET /api/admin/inspections/{id}/audit
 * (Plan 01 Task 3, HIST-5.3). Server returns rows ordered ASC by edited_at +
 * id ASC tie-break. CP4: `field_name` is a machine code from the server's
 * INSPECTION_EDITABLE_FIELD_NAMES allow-list — never carries credential
 * strings (Plan 01 log_inspection_edit triple-guard at the write site).
 *
 * `before_value` / `after_value` are typed as boolean | string | null because
 * the server's log_inspection_edit triple-guard rejects any other Python type
 * (Plan 04 Plan 01 SUMMARY § log_inspection_edit triple-guard CP4). The web
 * AuditTrailTable type-aware formatter renders booleans as Yes/No and null as
 * em-dash; coating strings use the existing LABEL_COATING_* localization.
 */
export interface InspectionAuditRow {
  id: number;
  /** ISO 8601 UTC server-recorded timestamp. */
  edited_at: string;
  edited_by: "pm" | "worker";
  field_name: string;
  before_value: boolean | string | null;
  after_value: boolean | string | null;
}

export interface InspectionAuditTrailResponse {
  rows: InspectionAuditRow[];
}

/**
 * Phase 6 Plan 02 — single photo row from GET /api/admin/inspections/{id}/photos
 * (Plan 01 Task 3, HIST-5.7). Includes both placeholder rows (uploaded_at IS
 * NULL) and landed rows (uploaded_at NOT NULL). Soft-deleted rows are excluded
 * server-side. Sort: placeholders first (uploaded_at NULLS FIRST), then by id.
 *
 * `client_photo_id` is the offline-mode client UUID (Phase 5 R3 placeholder
 * pattern) — null on PM-direct uploads. `storage_path` and `byte_size` are null
 * for placeholder rows (no file landed yet).
 */
export interface InspectionPhotoRow {
  id: number;
  client_photo_id: string | null;
  /** ISO 8601 UTC; null for placeholder (not-yet-landed) rows. */
  uploaded_at: string | null;
  /** Server-side filesystem path; null for placeholder rows. */
  storage_path: string | null;
  byte_size: number | null;
}

export interface InspectionPhotosResponse {
  photos: InspectionPhotoRow[];
}

/** Phase 13 D-04 — naming alias for PhotoStrip's PM-side mapping function. Same shape as InspectionPhotoRow. */
export type PmPhotoEntry = InspectionPhotoRow;

/**
 * PATCH body for /api/admin/inspections/{id}. Mirrors server's `InspectionEditIn`
 * which has `extra='forbid'` and an `applied_coating_requires_type` validator.
 *
 * worker_id / saved_at / gps_lat / gps_lng are INTENTIONALLY ABSENT — the
 * server's Pydantic schema rejects them with 422; the client type prevents
 * accidentally sending them in the first place (T-4-04 belt-and-suspenders).
 */
export interface InspectionEditPayload {
  tab1_cleaned?: boolean;
  tab1_structure_damaged?: boolean;
  tab1_cover_needs_level_adjustment?: boolean;
  tab1_pipe_blocked?: boolean;
  tab1_coating?: ObservedCoatingValue;
  tab2_adjusted_cover_level?: boolean;
  tab2_repaired_internal_structure?: boolean;
  tab2_applied_coating?: boolean;
  tab2_applied_coating_type?: CoatingValue;
}

export interface InspectionUpdatedResponse {
  code: "OK_INSPECTION_UPDATED";
  id: number;
  pm_edit_count: number;
}

export interface ClearPipeBlockedResponse {
  code: "OK_PIPE_BLOCKED_CLEARED";
  asset_id: number;
}

/** GET /api/admin/assets/{id}/inspections envelope (Plan 01 InspectionListResponse). */
export interface InspectionListResponse {
  inspections: InspectionRead[];
}
